import React, { useEffect, useState } from "react";
import { api } from "../../services/api";

// "Your access ends on …" (FORGE-408, Brennan 2026-10-29): shown across the
// top to a person whose OWN access has an end date (demo or temporary), never
// to Nuance staff and never about a client's contract end. The rows come from
// GET /api/manage/users/me (access_notices); Edge shows the same sentence from
// its copy (USER_ATTRIBUTES.accessEnds).

export interface AccessNotice {
  client: string;
  until: string; // YYYY-MM-DD, the last day (Central)
  kind: "demo" | "contract";
  days_left: number;
}

export const NUANCE_CONTACT = "info@nuance.team";

export function noticeDate(until: string): string {
  const [y, m, d] = until.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

export function noticeLeft(days: number): string {
  if (days <= 0) return "today is the last day";
  return days === 1 ? "1 day left" : `${days} days left`;
}

export function noticeText(n: AccessNotice, many: boolean): string {
  const what = n.kind === "demo" ? "demo access" : "temporary access";
  return `Your ${what}${many ? ` to ${n.client}` : ""} ends on ${noticeDate(n.until)} (${noticeLeft(n.days_left)}).`;
}

export function AccessEndsBanner({
  notices,
}: {
  notices: AccessNotice[] | null | undefined;
}) {
  if (!notices?.length) return null;
  const many = notices.length > 1;
  const urgent = notices.some((n) => n.days_left <= 3);
  return (
    <div
      role="status"
      className={`px-4 py-2 text-sm ${
        urgent
          ? "bg-amber-50 text-amber-900 border-b border-amber-300 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-700"
          : "bg-teal-50 text-teal-900 border-b border-teal-200 dark:bg-teal-900/30 dark:text-teal-100 dark:border-teal-800"
      }`}
    >
      {notices.map((n) => (
        <span key={n.client} className="mr-1">
          {noticeText(n, many)}
        </span>
      ))}
      If you need more time, contact the Nuance team at{" "}
      <a
        href={`mailto:${NUANCE_CONTACT}?subject=Access%20extension`}
        className="font-semibold underline"
      >
        {NUANCE_CONTACT}
      </a>
      .
    </div>
  );
}

// For pages outside the Elements/Forge shell (the client upload portal).
// Display only: a failed read shows no banner and never blocks the page.
export function MyAccessEndsBanner() {
  const [notices, setNotices] = useState<AccessNotice[] | null>(null);
  useEffect(() => {
    api
      .get<{ access_notices?: AccessNotice[] }>("/manage/users/me")
      .then((me) => setNotices(me?.access_notices ?? []))
      .catch(() => setNotices(null));
  }, []);
  return <AccessEndsBanner notices={notices} />;
}
